/* @jsx h */
import { computed, defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { weekdayNames } from "../WorkDay/shared/dates";
import { MonthCalendar } from "./MonthCalendar";
import type { LinkView } from "./types";

type Props = { token: string; view: LinkView; onSaved: () => void };

const monthLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

// "Work preferences" for an open month: desired hours, weekdays, dates.
export const Preferences = defineComponent<Props>({
  name: "Preferences",
  props: ["token", "view", "onSaved"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const names = weekdayNames("short");

    const month = ref(props.view.periods[0]?.month ?? "");
    const period = computed(() => props.view.periods.find((p) => p.month === month.value));
    const hours = ref<number>(0);
    const weekdays = ref<number[]>([]);
    const dates = ref<string[]>([]);
    const note = ref("");
    const saving = ref(false);

    watch(
      period,
      (p) => {
        const contract = props.view.me.contract_hours_week;
        hours.value = p?.desired_hours ?? (contract ? Math.round(contract * 4.33) : 120);
        weekdays.value = p?.weekdays ?? [];
        dates.value = p?.dates ?? [];
        note.value = p?.note ?? "";
      },
      { immediate: true },
    );

    const toggleDay = (d: number) => (weekdays.value = weekdays.value.includes(d) ? weekdays.value.filter((x) => x !== d) : [...weekdays.value, d].sort());
    const toggleDate = (d: string) => (dates.value = dates.value.includes(d) ? dates.value.filter((x) => x !== d) : [...dates.value, d].sort());

    const submit = async () => {
      saving.value = true;
      try {
        await trpc.teamLink.submitPreferences.mutate({
          token: props.token,
          month: month.value,
          desired_hours: hours.value,
          weekdays: weekdays.value,
          dates: dates.value,
          note: note.value.trim() || null,
        });
        toast.showToast(t("teamlink.page.prefs_saved"), { type: "success" });
        props.onSaved();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("teamlink.page.failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () => {
      if (!props.view.periods.length) {
        return (
          <div class="rounded-2xl bg-base-100 p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-base-content/30">event_note</span>
            <p class="mt-2 text-sm text-base-content/60">{t("teamlink.page.no_periods")}</p>
          </div>
        );
      }
      const p = period.value;
      return (
        <div class="space-y-4 rounded-2xl bg-base-100 p-5">
          <div class="flex items-center justify-between gap-2">
            <h2 class="text-xl font-semibold">{t("teamlink.page.work_preferences")}</h2>
            {props.view.periods.length > 1 ? (
              <select class="select select-bordered select-sm" value={month.value} onChange={(e: Event) => (month.value = (e.target as HTMLSelectElement).value)}>
                {props.view.periods.map((x) => (
                  <option key={x.month} value={x.month}>
                    {monthLabel(x.month)}
                  </option>
                ))}
              </select>
            ) : (
              <span class="text-sm text-base-content/60">{monthLabel(month.value)}</span>
            )}
          </div>
          {p?.submitted_at && (
            <div class="alert alert-success py-2 text-sm">
              <span class="material-symbols-outlined">check_circle</span>
              {t("teamlink.page.prefs_submitted_on").replace("{date}", new Date(p.submitted_at).toLocaleDateString())}
            </div>
          )}

          <div class="flex items-center justify-between rounded-xl bg-base-200 p-4">
            <div>
              <div class="font-medium">{t("teamlink.page.desired_hours")}</div>
              <div class="text-xs text-base-content/60">{t("teamlink.page.desired_hours_hint")}</div>
            </div>
            <div class="flex items-center gap-2">
              <button class="btn btn-circle btn-sm" aria-label="−" onClick={() => (hours.value = Math.max(0, hours.value - 4))}>
                <span class="material-symbols-outlined">remove</span>
              </button>
              <span class="w-20 text-center text-3xl font-semibold tabular-nums">{`${hours.value}h`}</span>
              <button class="btn btn-circle btn-sm" aria-label="+" onClick={() => (hours.value = hours.value + 4)}>
                <span class="material-symbols-outlined">add</span>
              </button>
            </div>
          </div>

          <div>
            <div class="mb-2 text-sm font-medium">{t("teamlink.page.preferred_days")}</div>
            <div class="grid grid-cols-7 gap-1.5">
              {names.map((n, i) => (
                <button
                  key={n}
                  class={["btn btn-sm px-0", weekdays.value.includes(i + 1) ? "btn-primary" : "btn-ghost border-base-300"].join(" ")}
                  onClick={() => toggleDay(i + 1)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div class="mb-2 flex items-baseline justify-between text-sm">
              <span class="font-medium">{t("teamlink.page.preferred_dates")}</span>
              <span class="text-xs text-base-content/50">{t("teamlink.page.dates_count").replace("{count}", String(dates.value.length))}</span>
            </div>
            <MonthCalendar month={month.value} selected={dates.value} weekdays={weekdays.value} onToggle={toggleDate} />
          </div>

          <textarea class="textarea textarea-bordered w-full text-sm" rows={2} placeholder={t("teamlink.page.note_placeholder")} v-model={note.value} />

          <button class="btn btn-primary btn-block" disabled={saving.value} onClick={submit}>
            {p?.submitted_at ? t("teamlink.page.update_preferences") : t("teamlink.page.submit_preferences")}
          </button>
        </div>
      );
    };
  },
});
